import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

export default function VehiclesSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const vehicles = [
    {
      name: 'Mercedes Classe E',
      category: 'Berline Premium',
      price: 'À partir de 220€',
      image: 'https://static.readdy.ai/image/ad5ba98ef3f5927eb89f8999f57293c0/711359abb222800b451f86b0927544a9.png',
      description: 'Élégance et confort pour vos trajets professionnels et privés',
      features: [
        { icon: 'ri-group-line', text: '3 passagers' },
        { icon: 'ri-luggage-cart-line', text: 'Bagages ski' },
        { icon: 'ri-wifi-line', text: 'WiFi gratuit' },
        { icon: 'ri-temp-hot-line', text: 'Sièges chauffants' }
      ]
    },
    {
      name: 'Mercedes Classe V',
      category: 'Van Premium',
      price: 'À partir de 270€',
      image: 'https://public.readdy.ai/ai/img_res/38992e613212a9550613ac9b7096c6d1.jpg',
      description: 'Espace et confort pour les groupes et familles',
      features: [
        { icon: 'ri-group-line', text: '7 passagers' },
        { icon: 'ri-layout-grid-line', text: 'Espace XXL' },
        { icon: 'ri-temp-cold-line', text: 'Climatisation' },
        { icon: 'ri-riding-line', text: 'Équipement ski' }
      ]
    },
    {
      name: 'Mercedes Classe S',
      category: 'Luxe Absolu',
      price: 'Sur demande',
      image: 'https://static.readdy.ai/image/ad5ba98ef3f5927eb89f8999f57293c0/f301b3d45e7aa9d2f3b86f0bfbef73b4.jpeg',
      description: 'Le summum du raffinement pour une expérience exceptionnelle',
      features: [
        { icon: 'ri-vip-crown-line', text: '3 passagers VIP' },
        { icon: 'ri-star-line', text: 'Confort maximal' },
        { icon: 'ri-goblet-line', text: 'Champagne offert' },
        { icon: 'ri-award-line', text: 'Service premium' }
      ]
    }
  ];

  return (
    <section ref={ref} id="vehicules" className="py-16 sm:py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-voyage-gold/10 px-4 py-2 rounded-full mb-6"
          >
            <i className="ri-car-line text-voyage-gold"></i>
            <span className="text-night-blue font-inter text-sm font-semibold">Nos Véhicules</span>
          </motion.div>
          
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-night-blue mb-4">
            Notre Flotte Premium
          </h2>
          <p className="text-slate-600 font-inter text-base sm:text-lg max-w-2xl mx-auto">
            Des véhicules haut de gamme pour votre confort et sécurité
          </p>
        </motion.div>

        {/* Grille de véhicules */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {vehicles.map((vehicle, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -10, boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-slate-200 group cursor-pointer"
            >
              {/* Badge prix */}
              <div className="relative">
                <div className="absolute top-4 right-4 bg-voyage-gold text-night-blue px-4 py-2 rounded-full font-inter font-bold text-sm shadow-lg z-10 whitespace-nowrap">
                  {vehicle.price}
                </div>
                
                {/* Image du véhicule */}
                <div className="relative h-56 sm:h-64 overflow-hidden bg-slate-100">
                  <img
                    src={vehicle.image}
                    alt={`${vehicle.name} - Véhicule de transfert privé premium pour stations de ski`}
                    title={vehicle.name}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </div>

              {/* Contenu */}
              <div className="p-6 sm:p-8">
                <div className="mb-4">
                  <p className="text-voyage-gold font-inter text-sm font-semibold mb-2">
                    {vehicle.category}
                  </p>
                  <h3 className="font-playfair text-2xl sm:text-3xl font-bold text-night-blue mb-3">
                    {vehicle.name}
                  </h3>
                  <p className="text-slate-600 font-inter text-sm sm:text-base leading-relaxed">
                    {vehicle.description}
                  </p>
                </div>

                {/* Caractéristiques */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {vehicle.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className={`${feature.icon} text-voyage-gold text-lg`}></i>
                      </div>
                      <span className="font-inter text-night-blue text-xs sm:text-sm font-medium">
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Bouton */}
                <motion.a
                  href="/reservation"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-voyage-gold text-night-blue rounded-full font-inter font-bold text-sm hover:bg-night-blue hover:text-white transition-all shadow-md whitespace-nowrap cursor-pointer group"
                >
                  Réserver ce véhicule
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </motion.a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Section Savoir-Faire */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 sm:mt-20"
        >
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Texte principal */}
            <div className="lg:col-span-2">
              <h3 className="font-playfair text-3xl sm:text-4xl font-bold text-night-blue mb-6">
                Savoir-Faire
              </h3>
              <div className="space-y-4 text-slate-700 font-inter text-base leading-relaxed">
                <p>
                  Notre service de <strong>chauffeur privé</strong> se distingue par une prestation adaptée à vos besoins. Nous mettons à votre disposition des <strong>véhicules haut de gamme</strong> pour vos déplacements <strong>privés ou professionnels</strong>. Nous offrons un service d'excellence avec professionnalisme en tout en proposant une gamme de véhicules premium.
                </p>
                <p>
                  Nous mettons à disposition des véhicules récents, soigneusement entretenus, et conduits par des chauffeurs professionnels et expérimentés. Grâce à notre <strong>service client</strong>, nous assurons vos déplacements dans les meilleurs conditions.
                </p>
                <p className="font-semibold text-night-blue">
                  Confort, élégance et sécurité, pour vous offrir une expérience agréable.
                </p>
              </div>
            </div>

            {/* Services offerts */}
            <div className="bg-gradient-to-br from-voyage-gold/10 to-amber-500/5 rounded-2xl p-6 sm:p-8 border border-voyage-gold/20">
              <h4 className="font-inter font-bold text-night-blue text-lg mb-6 uppercase tracking-wide">
                Services offerts à bord
              </h4>
              <ul className="space-y-3">
                {[
                  'Wi-Fi gratuit',
                  'Chargeur téléphone',
                  'Bouteilles d\'eau',
                  'Plaid chaud',
                  'Siège auto (sur demande)'
                ].map((service, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-700 font-inter text-sm sm:text-base">
                    <i className="ri-check-line text-voyage-gold text-xl flex-shrink-0"></i>
                    <span>{service}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bouton CTA */}
          <div className="text-center mt-12">
            <motion.a
              href="/reservation"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center justify-center gap-3 px-10 py-4 bg-voyage-gold text-night-blue rounded-full font-inter font-bold text-lg hover:bg-night-blue hover:text-white transition-all shadow-xl whitespace-nowrap cursor-pointer group"
            >
              <i className="ri-calendar-check-line text-xl"></i>
              Réserver une course
              <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
